import React from 'react';
import {
  Moon,
  Sun,
  Laptop,
  Building2,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';
import { useTheme } from '../../shared/context/ThemeContext';
import { Card } from '../../shared/components/Card';
import { UNIVERSITY_CONFIG } from '../../config/university.config';
import { DOCUMENT_DEFAULTS } from '../../config/document.config';

export const SettingsView: React.FC = () => {
  const { theme, setTheme } = useTheme();

  return (
    <div style={{ padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>
          Application Settings & Preferences
        </h1>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Manage interface theme, document defaults, and institutional environment parameters.
        </p>
      </div>

      {/* 1. Appearance Section */}
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
          Interface Appearance
        </h3>
        <div>
          <label className="form-label" style={{ marginBottom: 'var(--space-2)', display: 'block' }}>
            Theme Preference
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
            {[
              { id: 'light', label: 'Light Mode', icon: <Sun size={18} /> },
              { id: 'dark', label: 'Dark Mode', icon: <Moon size={18} /> },
              { id: 'auto', label: 'Auto (System OS)', icon: <Laptop size={18} /> },
            ].map((item) => {
              const isSelected = item.id === 'auto'
                ? !localStorage.getItem('sds_theme')
                : theme === item.id && !!localStorage.getItem('sds_theme');
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === 'auto') {
                      localStorage.removeItem('sds_theme');
                      const sysDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
                      setTheme(sysDark ? 'dark' : 'light');
                    } else {
                      setTheme(item.id as 'light' | 'dark');
                    }
                  }}
                  className={`btn ${isSelected ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ justifyContent: 'center', padding: 'var(--space-3)' }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* 2. Document Defaults Section */}
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
          Academic Document Defaults
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Default Paper Format</label>
            <select className="form-select" defaultValue={DOCUMENT_DEFAULTS.paperSize}>
              {DOCUMENT_DEFAULTS.supportedPaperSizes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Default Typography</label>
            <select className="form-select" defaultValue={DOCUMENT_DEFAULTS.fontFamily}>
              {DOCUMENT_DEFAULTS.supportedFonts.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Default Base Size</label>
            <input
              type="text"
              className="form-input"
              defaultValue={`${DOCUMENT_DEFAULTS.fontSizePt}pt (Academic Standard)`}
              readOnly
            />
          </div>

          <div className="form-group">
            <label className="form-label">Default Line Spacing</label>
            <input
              type="text"
              className="form-input"
              defaultValue={`${DOCUMENT_DEFAULTS.lineSpacing} (Standard Paragraph)`}
              readOnly
            />
          </div>
        </div>
      </Card>

      {/* 3. Institutional Configuration Summary */}
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Building2 size={18} color="var(--primary-600)" />
            <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
              Institutional Branding Profile
            </h3>
          </div>
          <span className="badge badge-success" style={{ fontSize: '10px' }}>
            <CheckCircle2 size={11} /> Configured
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>Institution Name:</span>
            <div style={{ fontWeight: 600 }}>{UNIVERSITY_CONFIG.name}</div>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>Acronym / Short Code:</span>
            <div style={{ fontWeight: 600 }}>{UNIVERSITY_CONFIG.shortName}</div>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>Portal URL:</span>
            <div style={{ fontWeight: 600 }}>{UNIVERSITY_CONFIG.website}</div>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>Default Department:</span>
            <div style={{ fontWeight: 600 }}>{UNIVERSITY_CONFIG.defaultDepartment}</div>
          </div>
        </div>
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
          * Institutional branding parameters are managed centrally in <code>src/config/university.config.ts</code>.
        </p>
      </Card>

      {/* 4. Application Architecture & Privacy Status */}
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
          <ShieldCheck size={18} color="var(--color-success)" />
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            Security & Platform Architecture
          </h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <p>
            <strong>Version:</strong> Student Document Studio 0.1.0-alpha (Stage 2 Shell)
          </p>
          <p>
            <strong>Hosting Mode:</strong> 100% Client-Side SPA (Compatible with Localhost, Static Hosting & Cloudflare Pages)
          </p>
          <p>
            <strong>Storage Boundary:</strong> Local Browser Sandbox (IndexedDB). No document data is sent to external servers.
          </p>
        </div>
      </Card>
    </div>
  );
};
